import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import CandidateApplicationsPanel from "../components/CandidateApplicationsPanel";
import { useEffect, useRef, useState } from "react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import ConsultationCancellation from "../components/ConsultationCancellation";
import CandidateProfileForm from "@/components/CandidateProfileForm";

export default function Dashboard() {
  const { user } = useAuth();
  return <DashboardContent key={JSON.stringify([user?.id, user?.tenant_id])} />;
}

function DashboardContent() {
  const { user, profile, hasRole, refresh } = useAuth();
  const [bookingUpdates, setBookingUpdates] = useState({});
  const [cvs, cvsState, retryCvs] = useDashboardList("/cvs", !!user);
  const [bookings, bookingsState, retryBookings] = useDashboardList("/consultations/bookings/mine", !!user);
  const [cases, casesState, retryCases] = useDashboardList("/verification/cases/mine", !!user);
  const [enrolments, enrolmentsState, retryEnrolments] = useDashboardList("/enrolments/mine", !!user);
  const [reports, reportsState, retryReports] = useDashboardList("/verification/reports/mine", !!user);
  const [notifications, notificationsState, retryNotifications] = useDashboardList("/notifications/mine", !!user);
  const [hireflow, setHireflow] = useState(null);
  const [programmeCases, setProgrammeCases] = useState([]);
  const [programmeDocs, setProgrammeDocs] = useState({});
  const [programmeFiles, setProgrammeFiles] = useState({});
  const [programmeMsg, setProgrammeMsg] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationMsg, setVerificationMsg] = useState("");
  const [programmeState, setProgrammeState] = useState({ loading: true, error: "" });
  const [programmeReload, setProgrammeReload] = useState(0);
  const verificationPending = useRef(false);
  const uploadPending = useRef(false);
  const downloadPending = useRef(false);
  const [verificationAction, setVerificationAction] = useState("");
  const [verificationError, setVerificationError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [programmeError, setProgrammeError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState("");
  const [downloadError, setDownloadError] = useState(false);

  useEffect(() => {
    if (!user) return;
    let active = true;
    api.get("/integrations/hireflow/candidate-status").then((r) => {
      if (active) setHireflow(r.data);
    }).catch(() => {
      if (active) setHireflow({ available: false, status: "temporarily_unavailable", message: "HireFlow updates are temporarily unavailable." });
    });
    return () => { active = false; };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    setProgrammeState({ loading: true, error: "" });
    api.get("/programmes/cases/mine").then(async (r) => {
      if (!Array.isArray(r.data)) throw new Error("Invalid programme case response");
      const entries = await Promise.all(r.data.map(async (c) => {
        const [requirements, files] = await Promise.all([
          api.get(`/programmes/cases/${c.id}/document-requirements`),
          api.get(`/programmes/cases/${c.id}/documents`),
        ]);
        if (!Array.isArray(requirements.data) || !Array.isArray(files.data)) throw new Error("Invalid programme documents response");
        return [c.id, requirements.data, files.data];
      }));
      if (!active) return;
      setProgrammeCases(r.data);
      setProgrammeDocs(Object.fromEntries(entries.map(([id, requirements]) => [id, requirements])));
      setProgrammeFiles(Object.fromEntries(entries.map(([id, , files]) => [id, files])));
      setProgrammeState({ loading: false, error: "" });
    }).catch((error) => {
      if (active) setProgrammeState({ loading: false, error: applicationErrorMessage(error, "Unable to load programme documents. Please try again.") });
    });
    return () => { active = false; };
  }, [user, programmeReload]);

  const uploadProgrammeDocument = async (caseId, requirement, file) => {
    if (!file || uploadPending.current) return;
    uploadPending.current = true;
    setUploading(true);
    setProgrammeMsg("");
    setProgrammeError(false);
    const fd = new FormData();
    fd.append("requirement_id", requirement.id);
    fd.append("file", file);
    try {
      const { data } = await api.post(`/programmes/cases/${caseId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProgrammeDocs((current) => ({ ...current, [caseId]: (current[caseId] || []).map((item) => item.id === requirement.id ? { ...item, status: "received" } : item) }));
      setProgrammeFiles((current) => ({ ...current, [caseId]: [data, ...(current[caseId] || [])] }));
      setProgrammeMsg("Document uploaded securely and marked as received.");
    } catch (e) {
      setProgrammeError(true);
      setProgrammeMsg(applicationErrorMessage(e, "Document upload failed. Select the file again to retry."));
    } finally {
      uploadPending.current = false;
      setUploading(false);
    }
  };

  const blob = async (url, filename) => {
    if (downloadPending.current) return;
    downloadPending.current = true;
    setDownloading(true);
    setDownloadMessage("Preparing your download…");
    setDownloadError(false);
    let href;
    try {
      const res = await api.get(url, { responseType: "blob" });
      href = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = href;
      link.download = filename;
      document.body.appendChild(link);
      try { link.click(); } finally { link.remove(); }
      setDownloadMessage("Download started.");
    } catch (error) {
      setDownloadError(true);
      setDownloadMessage(applicationErrorMessage(error, "Unable to download this file. Please try again."));
    } finally {
      if (href) URL.revokeObjectURL(href);
      downloadPending.current = false;
      setDownloading(false);
    }
  };

  if (!user) return <p className="p-10 text-sm">Please sign in to view your dashboard.</p>;

  const createCv = () => { window.location.href = "/cv-builder"; };
  const requestEmailVerification = async () => {
    if (verificationPending.current) return;
    verificationPending.current = true;
    setVerificationAction("request");
    setVerificationMsg("");
    setVerificationError(false);
    try {
      const { data } = await api.post("/auth/verify-email/request");
      setVerificationMsg(data.message);
    } catch (error) {
      setVerificationError(true);
      setVerificationMsg(applicationErrorMessage(error, "Unable to send the verification email."));
    } finally {
      verificationPending.current = false;
      setVerificationAction("");
    }
  };
  const confirmEmailVerification = async () => {
    if (!verificationCode.trim() || verificationPending.current) return;
    verificationPending.current = true;
    setVerificationAction("confirm");
    setVerificationMsg("");
    setVerificationError(false);
    try {
      await api.post("/auth/verify-email/confirm", { token: verificationCode.trim() });
      setVerificationCode("");
      setVerificationMsg("Email verified successfully.");
      try { await refresh(); } catch {
        setVerificationMsg("Email verified successfully. Refresh the page to update your account.");
      }
    } catch (error) {
      setVerificationError(true);
      setVerificationMsg(applicationErrorMessage(error, "Unable to verify this code."));
    } finally {
      verificationPending.current = false;
      setVerificationAction("");
    }
  };
  return (
    <div className="max-w-7xl mx-auto px-5 py-10" data-testid="dashboard-page">
      <p className="overline">Signed in as {user.email}</p>
      <h1 className="mt-3 text-3xl font-semibold">Your EuropEdu dashboard</h1>
      <div className="mt-4 flex flex-wrap gap-2"><Link className="btn btn-primary" to="/ai-applications">AI application workspace</Link><Link className="btn btn-ghost" to="/study">Study search and applications</Link><Link className="btn btn-ghost" to="/jobs/saved">Saved jobs and alerts</Link><Link className="btn btn-ghost" to="/visa-support">Visa support requests</Link><Link className="btn btn-ghost" to="/verify">Verification applications</Link></div>
      <div className="mt-2 flex flex-wrap gap-2">{user.roles.map((r) => <span key={r} className="pill bg-blue-50 text-blue-800">{r.replace(/_/g, " ")}</span>)}</div>

      {downloadMessage && <p role={downloadError ? "alert" : "status"} className={downloadError ? "mt-4 text-sm text-red-700" : "mt-4 text-sm text-slate-600"}>{downloadMessage}</p>}
      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        {!user.email_verified && <section className="card border-amber-200 bg-amber-50 p-6 lg:col-span-3" data-testid="email-verification-panel"><h2 className="text-lg font-semibold">Verify your email address</h2><p className="mt-2 text-sm text-slate-600">Verification is required before submitting programme assessments.</p><div className="mt-4 flex flex-wrap gap-2"><button type="button" className="btn btn-primary" disabled={!!verificationAction} onClick={requestEmailVerification}>{verificationAction === "request" ? "Sending code…" : "Send verification code"}</button><input aria-label="Email verification code" className="field max-w-sm" disabled={!!verificationAction} value={verificationCode} onChange={(event) => setVerificationCode(event.target.value)} placeholder="Enter the code from your email" /><button type="button" className="btn btn-ghost" disabled={!!verificationAction || !verificationCode.trim()} onClick={confirmEmailVerification}>{verificationAction === "confirm" ? "Verifying…" : "Confirm code"}</button></div>{verificationMsg && <p role={verificationError ? "alert" : "status"} className={verificationError ? "mt-3 text-sm text-red-700" : "mt-3 text-sm"}>{verificationMsg}</p>}</section>}
        <CandidateProfileForm profile={profile} refresh={refresh} />

        <CandidateApplicationsPanel key={JSON.stringify([user.id, user.tenant_id])} />

        <section className="card p-6" data-testid="cvs-panel">
          <h2 className="text-lg font-semibold">CV versions{!cvsState.loading && !cvsState.error ? " (" + cvs.length + ")" : ""}</h2>
          <DashboardResource state={cvsState} onRetry={retryCvs}>
          <ul className="mt-3 space-y-1 text-sm">{cvs.map((c) => <li key={c.id}>{c.name} — v{c.version} ({c.template})</li>)}</ul>
          <button data-testid="create-cv-button" onClick={createCv} className="btn btn-ghost mt-4 w-full justify-center">Create a UK CV</button>
          </DashboardResource>
        </section>

        <section className="card p-6" data-testid="bookings-panel">
          <h2 className="text-lg font-semibold">Consultations</h2>
          <DashboardResource state={bookingsState} onRetry={retryBookings}>
          <ul className="mt-3 space-y-2 text-sm">
            {bookings.map((row) => { const b = bookingUpdates[row.id] || row; return (
              <li key={b.id} className="border-b border-slate-100 pb-2">
                <p>{new Date(b.start_at).toLocaleString("en-GB")} · {b.consultant_name} · {b.status} · {money(b.price_pence)}</p>
                {b.status === "confirmed" && (
                  <button data-testid={`booking-ics-${b.id}`} disabled={downloading} onClick={() => blob(`/consultations/bookings/${b.id}/calendar.ics`, `europedu-consultation.ics`)}
                    className="btn btn-ghost mt-1 py-1 px-3">Add to calendar</button>
                )}
                <ConsultationCancellation booking={b} onCancelled={(saved) => setBookingUpdates((current) => ({ ...current, [saved.id]: saved }))} />
              </li>
            ); })}
            {bookings.length === 0 && <li className="text-slate-500">No consultations booked.</li>}
          </ul>
          </DashboardResource>
        </section>

        <section className="card p-6" data-testid="cases-panel">
          <h2 className="text-lg font-semibold">Verification cases</h2>
          <DashboardResource state={casesState} onRetry={retryCases}>
          <ul className="mt-3 space-y-2 text-sm">
            {cases.map((c) => <li key={c.id}>{c.case_number} · {c.scope_name} · {c.status}</li>)}
            {cases.length === 0 && <li className="text-slate-500">No cases opened.</li>}
          </ul>
          </DashboardResource>
        </section>

        <section className="card p-6 lg:col-span-2" data-testid="programme-cases-panel">
          <h2 className="text-lg font-semibold">Programme documents</h2>
          <DashboardResource state={programmeState} onRetry={() => { setProgrammeState({ loading: true, error: "" }); setProgrammeReload((value) => value + 1); }}>
          <p className="mt-1 text-sm text-slate-500">Upload requested documents securely. Files are scanned before release.</p>
          {uploading && <p role="status" className="mt-3 text-sm text-slate-600">Uploading document…</p>}
          {programmeMsg && <p role={programmeError ? "alert" : "status"} className={programmeError ? "mt-3 text-sm text-red-700" : "mt-3 text-sm text-emerald-700"}>{programmeMsg}</p>}
          {programmeCases.length === 0 && <p className="mt-4 text-sm text-slate-500">No programme cases linked to this account.</p>}
          <div className="mt-4 space-y-4">{programmeCases.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 p-4">
              <p className="font-medium">{c.programme_slug} · {c.status}</p>
              {(programmeDocs[c.id] || []).map((d) => <div key={d.id} className="mt-3 rounded-lg bg-slate-50 p-3 text-sm"><div className="flex flex-wrap items-center justify-between gap-3"><span>{d.title} · <span className="text-slate-500">{d.status}</span></span>{d.status !== "approved" && <input aria-label={`Upload ${d.title}`} type="file" disabled={uploading} accept=".pdf,.docx,.jpg,.jpeg,.png" onChange={(e) => { const file = e.target.files[0]; e.target.value = ""; uploadProgrammeDocument(c.id, d, file); }} />}</div>{(programmeFiles[c.id] || []).filter((file) => file.requirement_id === d.id).map((file) => <button key={file.id} type="button" className="mt-2 block text-xs text-blue-700 underline" disabled={downloading} onClick={() => blob(`/files/${file.id}/download`, file.original_filename)}>{file.original_filename} · {file.malware_scan_status}</button>)}</div>)}
            </div>
          ))}</div>
          </DashboardResource>
        </section>

        <section className="card p-6" data-testid="reports-panel">
          <h2 className="text-lg font-semibold">Verification reports{!reportsState.loading && !reportsState.error ? " (" + reports.length + ")" : ""}</h2>
          <DashboardResource state={reportsState} onRetry={retryReports}>
          <ul className="mt-3 space-y-2 text-sm">
            {reports.map((r) => (
              <li key={r.id} className="border-b border-slate-100 pb-2">
                <p className="font-medium">{r.reference} · {r.classification.replace(/_/g, " ")}</p>
                <p className="text-xs text-slate-500">Validation code {r.validation_code}</p>
                <button data-testid={`download-report-${r.id}`} disabled={downloading} onClick={() => blob(`/verification/reports/${r.id}/download`, `${r.reference}.pdf`)}
                  className="btn btn-ghost mt-1 py-1 px-3">Download report PDF</button>
              </li>
            ))}
            {reports.length === 0 && <li className="text-slate-500">No reports issued yet.</li>}
          </ul>
          </DashboardResource>
        </section>

        <section className="card p-6" data-testid="notifications-panel">
          <h2 className="text-lg font-semibold">Notifications</h2>
          <DashboardResource state={notificationsState} onRetry={retryNotifications}>
          <ul className="mt-3 space-y-2 text-sm">
            {notifications.slice(0, 8).map((n) => (
              <li key={n.id} className="flex justify-between gap-2">
                <span className="truncate">{n.subject}</span>
                <span className="text-xs text-slate-500 shrink-0">{(n.delivery_status || "").replace(/_/g, " ")}</span>
              </li>
            ))}
            {notifications.length === 0 && <li className="text-slate-500">No notifications yet.</li>}
          </ul>
          </DashboardResource>
        </section>


        <section className="card p-6 lg:col-span-2" data-testid="hireflow-status-panel">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">HireFlow application updates</h2>
              <p className="mt-1 text-sm text-slate-500">Read-only status shared from your linked recruitment record.</p>
            </div>
            {hireflow?.available && <span className="pill bg-emerald-50 text-emerald-700">Synced</span>}
          </div>
          {!hireflow?.available && <p className="mt-4 text-sm text-slate-500">{hireflow?.message || "Checking your HireFlow connection…"}</p>}
          {hireflow?.available && <>
            <p className="mt-4 text-sm"><span className="font-medium">Current stage:</span> {hireflow.stage_label || hireflow.stage || "Not specified"}</p>
            {hireflow.stage_updated_at && <p className="mt-1 text-xs text-slate-500">Updated {new Date(hireflow.stage_updated_at).toLocaleString("en-GB")}</p>}
            <div className="mt-4 grid gap-3 sm:grid-cols-3 text-sm">
              <div className="rounded-lg bg-slate-50 p-3"><p className="font-medium">Timeline</p><p className="mt-1 text-slate-600">{hireflow.events?.length || 0} updates</p></div>
              <div className="rounded-lg bg-slate-50 p-3"><p className="font-medium">Documents</p><p className="mt-1 text-slate-600">{hireflow.documents?.length || 0} records</p></div>
              <div className="rounded-lg bg-slate-50 p-3"><p className="font-medium">Payments</p><p className="mt-1 text-slate-600">{hireflow.payments?.length || 0} records</p></div>
            </div>
          </>}
        </section>
        <section className="card p-6" data-testid="enrolments-panel">
          <h2 className="text-lg font-semibold">Course enrolments</h2>
          <DashboardResource state={enrolmentsState} onRetry={retryEnrolments}>
          <ul className="mt-3 space-y-2 text-sm">
            {enrolments.map((e) => <li key={e.id}>{e.course_title} · {e.progress}% complete</li>)}
            {enrolments.length === 0 && <li className="text-slate-500">No enrolments.</li>}
          </ul>
          </DashboardResource>
        </section>
      </div>

      {hasRole("employer", "recruiter") && (
        <p className="mt-8 text-sm"><Link className="btn btn-primary" to="/employer" data-testid="employer-portal-link">Open employer portal</Link></p>
      )}
      {hasRole("sales_partner") && (
        <p className="mt-4 text-sm"><Link className="btn btn-ghost" to="/partners" data-testid="partner-portal-link">Open partner portal</Link></p>
      )}
    </div>
  );
}


function DashboardResource({ state, onRetry, children }) {
  if (state.loading) return <p role="status" className="mt-3 text-sm text-slate-500">Loading…</p>;
  if (state.error) return <>
    <p role="alert" className="mt-3 text-sm text-red-700">{state.error}</p>
    <button type="button" className="btn btn-ghost mt-3" onClick={onRetry}>Retry loading</button>
  </>;
  return children;
}

function useDashboardList(endpoint, enabled) {
  const [items, setItems] = useState([]);
  const [state, setState] = useState({ loading: true, error: "" });
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setState({ loading: true, error: "" });
    api.get(endpoint).then(({ data }) => {
      if (!active) return;
      if (!Array.isArray(data)) throw new Error("Invalid dashboard response");
      setItems(data);
      setState({ loading: false, error: "" });
    }).catch((error) => {
      if (active) setState({ loading: false, error: applicationErrorMessage(error, "Unable to load this section. Please try again.") });
    });
    return () => { active = false; };
  }, [endpoint, enabled, reload]);
  const retry = () => {
    setState({ loading: true, error: "" });
    setReload((value) => value + 1);
  };
  return [items, state, retry];
}

